import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react'
import { apiFetch, setTokens, clearTokens, getStoredRefreshToken } from '../utils/api'

interface User {
  id: string
  email: string
  firstName: string
  lastName: string
  role: string
  company: {
    id: string
    slug: string
    name: string
  }
}

interface AuthState {
  user: User | null
  loading: boolean
  login: (email: string, password: string) => Promise<string | null>
  register: (data: RegisterData) => Promise<string | null>
  logout: () => void
}

interface RegisterData {
  email: string
  password: string
  firstName: string
  lastName: string
  companyName: string
  companySlug: string
}

const AuthContext = createContext<AuthState | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)

  // Try to restore session on mount
  useEffect(() => {
    const rt = getStoredRefreshToken()
    if (!rt) {
      setLoading(false)
      return
    }

    apiFetch<{ accessToken: string }>('/auth/refresh', {
      method: 'POST',
      body: JSON.stringify({ refreshToken: rt }),
    }).then(res => {
      if (res.data) {
        setTokens(res.data.accessToken, rt)
        return apiFetch<{ user: User }>('/auth/me')
      }
      clearTokens()
      return null
    }).then(res => {
      if (res?.data) setUser(res.data.user)
    }).finally(() => setLoading(false))
  }, [])

  const login = useCallback(async (email: string, password: string): Promise<string | null> => {
    const res = await apiFetch<{
      accessToken: string
      refreshToken: string
      user: User
    }>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    })

    if (res.error) return res.error

    if (res.data) {
      setTokens(res.data.accessToken, res.data.refreshToken)
      setUser(res.data.user)
    }
    return null
  }, [])

  const register = useCallback(async (data: RegisterData): Promise<string | null> => {
    const res = await apiFetch<{
      accessToken: string
      refreshToken: string
      user: User
    }>('/auth/register', {
      method: 'POST',
      body: JSON.stringify(data),
    })

    if (res.error) return res.error

    if (res.data) {
      setTokens(res.data.accessToken, res.data.refreshToken)
      setUser(res.data.user)
    }
    return null
  }, [])

  const logout = useCallback(() => {
    clearTokens()
    setUser(null)
  }, [])

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}
